import groupBy from "lodash/groupBy";
import range from "lodash/range";
import * as React from "react";
import { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { ThunkDispatch } from "core-integration/src/store";
import {
  getElementTypes,
  getElementTypesById,
} from "core-integration/src/store/element-types/reducers";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import {
  IElement,
  IElementControls,
} from "core-integration/src/store/elements/types";
import {
  getProposedElementsById,
  getTypesNeeded,
} from "core-integration/src/store/squad/reducers";
import {
  proposeElement,
  removeElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import Alert from "../Alert";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import ElementFilter from "../element-controls/ElementFilter";
import ElementSort from "../element-controls/ElementSort";
import Paginator from "../element-controls/Paginator";
import ElementListRow from "../ElementListRow";
import { ElementTable } from "../ElementTable";
import { FieldWrap, SearchField, SelectField } from "../FieldRenderers";
import { ControlArrowLeft } from "../icons/Arrows";
import Panel from "../Panel";
import SubHeading from "../SubHeading";
import Tooltip, { TooltipLabel } from "../Tooltip";

const Form = styled.form`
  padding: 0 ${({ theme }) => theme.space[2]};
`;

const ElementsShown = styled.p`
  margin: ${({ theme }) => theme.space[2]} 0;
  text-align: center;

  strong {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ElementListElement = styled.th`
  width: 46%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 50%;
  }
`;

const ElementListPrice = styled.th`
  width: 22%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 18%;
  }
`;

const ElementListStat = styled.th`
  width: 22%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 18%;
  }
`;

const ElementTypeButton = styled.span`
  padding: 0;
  border: 0;
  background-color: transparent;
  color: currentColor;
  font-size: ${({ theme }) => theme.fontSizes[2]};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.base};
`;

const ButtonWrap = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 50%;
    margin: 0 auto;
  }
`;

interface IOwnProps {
  hideRef: React.RefObject<HTMLButtonElement>;
  hideSidebar: () => void;
}

type Props = IOwnProps & WithTranslation;

const ElementList = ({ hideRef, hideSidebar, t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [elementForMenu, setElementForMenu] = useState<IElement | null>(null);
  const [page, setPage] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elementTypes = useSelector(getElementTypes);
  const elementTypesById = useSelector(getElementTypesById);
  const elements = useSelector(getElementsFromControls);
  const proposedElementsById = useSelector(getProposedElementsById);
  const teamsById = useSelector(getTeamsById);
  const typesNeeded = useSelector(getTypesNeeded);

  const handleAddElement = (elementId: number) => {
    dispatch(proposeElement(elementId));
    hideSidebar();
    handleHideMenuForElement();
  };

  const handleRemoveElement = (position: number) => {
    dispatch(removeElement(position));
    handleHideMenuForElement();
  };

  const updateControls = (controls: IElementControls) =>
    dispatch(updateElementControls(controls));

  const updateControlsAndMaxCost = (controls: IElementControls) =>
    dispatch(updateElementControlsAndMaxCost(controls));

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControlsAndMaxCost({
      ...controls,
      filter: e.target.value,
    });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      sort: e.target.value,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      search: e.target.value,
    });
  };

  const handleMaxCostChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      maxCost: Number(e.target.value),
    });
  };

  const handleShowMenuForElement = (element: IElement) =>
    setElementForMenu(element);

  const handleHideMenuForElement = () => {
    setElementForMenu(null);
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  useEffect(() => {
    updateControlsAndMaxCost({
      ...controls,
      filter: "all",
      sort: "total_points",
      search: "",
      getUnavailable: false,
    });
  }, []);

  const paginateAndGroup = (elements: IElement[], pageSize: number) => {
    const start = (page - 1) * pageSize;
    return {
      data: groupBy(elements.slice(start, start + pageSize), "element_type"),
      totalPages: Math.ceil(elements.length / pageSize),
    };
  };

  const renderMenu = () => {
    const element = elementForMenu;

    if (!element) {
      return null;
    }

    const proposedElement = proposedElementsById[element.id];
    const roomInSquad = typesNeeded[element.element_type];

    let alert = null;
    if (!roomInSquad) {
      alert = (
        <Alert type="error" noRadius>
          {t(
            "elementList.errors.maxElementType",
            "You already have the maximum number of {{ position }}",
            {
              position: elementTypesById[element.element_type].plural_name,
            },
          )}
        </Alert>
      );
    } else if (proposedElement) {
      alert = (
        <Alert type="warning" noRadius>
          <Trans
            i18nKey="elementList.errors.alreadySelected"
            values={{ playerName: element.web_name }}
          >
            <strong>{element.web_name}</strong> is already in your squad.
          </Trans>
        </Alert>
      );
    }

    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          <Box py={2} px={3}>
            <DialogHeading>
              {`${element.first_name} ${element.second_name}`}
            </DialogHeading>
          </Box>
        </Dialog.Header>
        {alert && alert}

        <Dialog.Body>
          <PlayerActions>
            <DialogButtonItem>
              {proposedElement ? (
                <Button
                  onClick={() => handleRemoveElement(proposedElement.position)}
                  width={1}
                >
                  {t("elementList.removePlayer", "Remove player")}
                </Button>
              ) : (
                <Button
                  onClick={() => handleAddElement(element.id)}
                  disabled={Boolean(!roomInSquad)}
                  width={1}
                >
                  {t("elementList.addPlayer", "Add player")}
                </Button>
              )}
            </DialogButtonItem>
            <DialogButtonItem>
              <Button onClick={() => showDialog(element)} width={1}>
                {t("viewInformation", "View information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const { data, totalPages } = paginateAndGroup(elements.data, 30);
  return (
    <div>
      <Panel>
        <Flex mx={2} mb={2} alignItems="center" justifyContent="space-between">
          <SubHeading>
            {t("elementList.playerSelection", "Player Selection")}
          </SubHeading>
          <ButtonWrap>
            <Button
              size="sm"
              ref={hideRef}
              onClick={hideSidebar}
              startIcon={<ControlArrowLeft />}
            >
              {t("back", "Back")}
            </Button>
          </ButtonWrap>
        </Flex>
        <Form onSubmit={(e) => e.preventDefault()}>
          <ElementFilter handleFilterChange={handleFilterChange} />
          <ElementSort handleSortChange={handleSortChange} />
          <FieldWrap>
            <SearchField
              id="search"
              name="search"
              label={t("elementList.search", "Search Player List")}
              onChange={handleSearchChange}
              value={controls.search}
              required={false}
            />
          </FieldWrap>
          <FieldWrap>
            <SelectField
              id="maxCost"
              name="maxCost"
              label={t("elementList.maxCost.label", "Max cost")}
              value={controls.maxCost}
              onChange={handleMaxCostChange}
              required={false}
            >
              {range(elements.maxCost, elements.minCost - 1, -5).map((cost) => (
                <option
                  key={cost}
                  value={cost}
                  aria-selected={controls.maxCost === Number(cost)}
                >
                  {integerToMoney(cost, 10)}
                </option>
              ))}
            </SelectField>
          </FieldWrap>
        </Form>
        <ElementsShown>
          <Trans
            i18nKey="elementList.playersShown"
            values={{ length: elements.data.length }}
            defaults={"<0>{{length}}</0> players shown"}
            components={[<strong />]}
          />
        </ElementsShown>
        {elementTypes.map(
          (et) =>
            data[et.id] && (
              <ElementTable key={et.id} elementType={et.id}>
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <ElementListElement>
                      <ElementTypeButton
                        onClick={() =>
                          dispatch(updateElementTypeControl(et.id))
                        }
                      >
                        {et.plural_name}
                      </ElementTypeButton>
                    </ElementListElement>
                    <ElementListPrice>
                      <Tooltip content={t("price", "Price")}>
                        <TooltipLabel>Mkr</TooltipLabel>
                      </Tooltip>
                    </ElementListPrice>
                    <ElementListStat>P</ElementListStat>
                  </tr>
                </thead>
                <tbody>
                  {data[et.id].map((e) => (
                    <ElementListRow
                      key={e.id}
                      element={e}
                      isProposed={proposedElementsById[e.id] ? true : false}
                      renderElementMenu={() => handleShowMenuForElement(e)}
                      renderElementDialog={() => showDialog(e)}
                      team={teamsById[e.team]}
                      currencyDivisor={currencyDivisor}
                      sort={controls.sort}
                    />
                  ))}
                </tbody>
              </ElementTable>
            ),
        )}
        {renderMenu()}
      </Panel>
      <Paginator totalPages={totalPages} page={page} setPage={setPage} />
    </div>
  );
};

export default withTranslation()(ElementList);
